/**
 * B795-wait (Pathik 2026-10-13, on the PAY step's "Payment received by your
 * card provider. We're waiting on the final order confirmation…" card:
 * "customers dont care. it should show clean animation with useful messages
 * like 'Processing' 'Hang Tight' 'Creating Tickets'") — the WAIT face between
 * the provider's yes and the kernel's confirmation.
 *
 * Three accent dots breathing (the brand's own ink, no spinner glyph) over
 * ONE line that walks the buyer's story: the payment, then the pause, then
 * the tickets — each step a few seconds, the last one holding honestly
 * ("Still working on it…") rather than looping back to the start as if
 * nothing had happened. SSR renders the first line; the walk runs only in
 * the browser. `role="status"` + `aria-live="polite"`: AT hears each step
 * once. Under prefers-reduced-motion the dots stand still and the words
 * still walk — the words are the point.
 *
 * The honest clause the old prose carried ("your seats are reserved and your
 * money is safe") stays, in the smaller register under the line: it is the
 * one sentence a buyer who is worried actually needs.
 */
import { useEffect, useState, type ReactNode } from "react";

/** The walk, in order; the last line holds. Exported for the storefront's pins. */
export const PAYMENT_WAIT_STEPS: readonly string[] = Object.freeze([
  "Processing your payment",
  "Hang tight — almost there",
  "Creating your tickets",
  "Still working on it",
]);

/** How long each line stands before the next (ms). */
export const PAYMENT_WAIT_STEP_MS = 2600;

/** Pure: the line for an elapsed time — the walk's one rule, testable without a clock. */
export function paymentWaitStep(elapsedMs: number, stepMs: number = PAYMENT_WAIT_STEP_MS): string {
  const i = Math.max(0, Math.floor(elapsedMs / stepMs));
  return PAYMENT_WAIT_STEPS[Math.min(i, PAYMENT_WAIT_STEPS.length - 1)] as string;
}

export function PaymentWait(props: {
  /** The quiet sentence under the line; absent = the default reassurance. */
  readonly note?: string;
  /** A sidecar hook; omitted = no attribute. */
  readonly redline?: string;
}): ReactNode {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (i >= PAYMENT_WAIT_STEPS.length - 1) return;
    const t = setTimeout(() => setI((n) => Math.min(n + 1, PAYMENT_WAIT_STEPS.length - 1)), PAYMENT_WAIT_STEP_MS);
    return () => clearTimeout(t);
  }, [i]);
  return (
    <div className="t-paywait" role="status" aria-live="polite" {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      <span className="t-paywait-dots" aria-hidden="true">
        <span className="t-paywait-dot" />
        <span className="t-paywait-dot" />
        <span className="t-paywait-dot" />
      </span>
      <p className="t-paywait-line">{PAYMENT_WAIT_STEPS[i]}…</p>
      <p className="t-paywait-note">{props.note ?? "Your seats are reserved and your money is safe."}</p>
    </div>
  );
}
